import React, { FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t, Trans } from "@lingui/macro";
import { useInputChange } from "hooks/useInputChange";

import { InputText } from "components/UI/InputText/InputText";
import { Button } from "components/UI/Button/Button";
import { ISystemState } from "model/system/systemReducer";

import {
  IdOrdersState,
  typeOrdersProductItem,
} from "model/dashboardOrders/dOrdersReducer";
import { PopupPanelDashboard } from "components/containers/dashboard/DashboardContentTable/PopupPanelDashboard";
import { InputSearch } from "components/UI/InputSearch/InputSearch";
import { PopupChangePrice } from "components/containers/dashboard/dataOrders/popupContents/popupChangePrice/popupChangePrice";
import { PopupEnterMark } from "components/containers/dashboard/dataOrders/popupContents/popupEnterMark/popupEnterMark";
import { Carts } from "components/containers/dashboard/dataOrders/Carts";
import { Products } from "components/containers/dashboard/dataOrders/Products";
import { Categories } from "components/containers/dashboard/dataOrders/Categories";
import {
  dOrdersAddItemSagaAC,
  dOrdersClearCartItemAC,
  dOrdersClearProductFiltersAC,
  dOrdersSetProductFiltersAC,
  dOrdersSetSelectedStoreAC,
} from "model/dashboardOrders/dOrdersActionCreators";
import { typeProductsFilters } from "model/dashboardProducts/dProductsReducer";
import {
  ChatAltIcon,
  ExclamationCircleIcon,
  LocationMarkerIcon,
  PencilAltIcon,
  PhoneIcon,
  TagIcon,
  UserAddIcon,
  UserIcon,
} from "@heroicons/react/outline";
import { InputTextarea } from "components/UI/InputTextarea/InputTextarea";

import { PopupSelectStore } from "components/containers/dashboard/dataOrders/popupContents/popupSelectStore";
import {
  addNotificationAC,
  logoutSagaAC,
} from "model/system/systemActionCreators";
import { NULL_VALUE, SelectBox } from "components/UI/SelectBox/SelectBox";
import { IdUsersStateDataItem } from "model/dashboardUsers/dUsersReducer";
import { getUsersList } from "model/dashboardUsers/dUsersAPI";
import { PopupDeleteMarkedProduct } from "components/containers/dashboard/dataOrders/popupContents/popupDeleteMarkedProduct/popupDeleteMarkedProduct";
import { USERPOSITIONS } from "constants/userPositions";
import { typeNewOrderRequest } from "model/dashboardOrders/dOrdersAPI";
import { ORDERSTATUS } from "constants/ordersStatus";
import { getNotDeletedCategoriesList } from "model/dashboardCategories/dCategoriesAPI";
import { IdCategoriesStateDataItem } from "model/dashboardCategories/dCategoriesReducer";
import { Loader } from "components/UI/Loader/Loader";

// Type
export type typePopupAdd = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

// Component
export const PopupAdd: React.FC<typePopupAdd> = ({ variant, setOpenPopup }) => {
  //system
  const filters = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.filters,
  );
  const pagination = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.pagination,
  );

  const [isOpenChangePrise, setOpenChangePrise] = useState<boolean>(false);
  const [isOpenEnterMark, setOpenEnterMark] = useState<boolean>(false);
  const [isOpenDeleteMarkedProduct, setOpenDeleteMarkedProduct] =
    useState<boolean>(false);

  // Popup select store
  const [openPopupSelectStore, setOpenPopupSelectStore] = useState(false);

  const dispatch = useDispatch();

  // System data
  const creatorName = useSelector(
    (state: { system: ISystemState }) => state.system.login,
  );

  const selectedStore = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.selectedStore,
  );

  const currentCart = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.newOrder,
  );

  // did mount
  useEffect(() => {
    if (!selectedStore) {
      setOpenPopupSelectStore(true);
    }
    // eslint-disable-next-line
  }, []);

  // did unmount
  useEffect(() => {
    return () => {
      dispatch(dOrdersClearProductFiltersAC());
      dispatch(dOrdersSetSelectedStoreAC(undefined));
      dispatch(dOrdersClearCartItemAC());
    };
    // eslint-disable-next-line
  }, []);

  const {
    value: clientNameValue,
    changeValueHandler: clientNameChangeValueHandler,
  } = useInputChange("");
  const {
    value: clientPhoneValue,
    changeValueHandler: clientPhoneChangeValueHandler,
  } = useInputChange("");

  const { value: addressValue, changeValueHandler: addressChangeValueHandler } =
    useInputChange("");

  const { value: commentValue, changeValueHandler: commentChangeValueHandler } =
    useInputChange("");

  const [courierIdValue, setCourierIdValue] = useState<string | null>(
    NULL_VALUE.id,
  );

  const [selectAllUsersList, setSelectUsersList] = useState<
    IdUsersStateDataItem[]
  >([]);
  const [loaderUsersList, setLoaderUsersList] = useState(false);

  const [categoryList, setCategoryList] = useState<IdCategoriesStateDataItem[]>(
    [],
  );
  const [loaderCategoryList, setLoaderCategoryList] = useState(false);

  useEffect(() => {
    if (selectedStore?.id) {
      setCourierIdValue(NULL_VALUE.id);
    }
  }, [selectedStore?.id]);

  // Get list of users and categories
  useEffect(() => {
    const setData = async () => {
      setLoaderUsersList(true);
      setLoaderCategoryList(true);
      let responseUsers = await getUsersList({
        filters: {
          deleted: false,
          position: ["COURIER", "JUNIOR_COURIER", "DISTRIBUTOR"],
        },
        full: false,
      });

      // filters response by position courier/jrCourier/Distributor
      if (responseUsers.status === 200) {
        const newManagersList: IdUsersStateDataItem[] =
          responseUsers.data.filter((item: IdUsersStateDataItem) => {
            return (
              item.position === USERPOSITIONS.COURIER ||
              item.position === USERPOSITIONS.JUNIOR_COURIER ||
              item.position === USERPOSITIONS.DISTRIBUTOR
            );
          });
        setSelectUsersList(newManagersList);
      } else if (responseUsers.status === 401) {
        dispatch(logoutSagaAC());
      }

      let responseCategories = await getNotDeletedCategoriesList();

      if (responseCategories.status === 200) {
        setCategoryList(responseCategories.data);
      } else if (responseCategories.status === 401) {
        dispatch(logoutSagaAC());
      }
      setLoaderUsersList(false);
      setLoaderCategoryList(false);
    };
    setData();

    // eslint-disable-next-line
  }, []);

  const [validationObject, setValidationObject] = useState({
    requiredClientName: true,
    requiredAddress: true,
    requiredClientPhone: true,
    validDiscount: true,
    validServicePayment: true,
  });

  const [isTouched, setIsTouched] = useState({
    clientNameTouched: false,
    clientPhoneTouched: false,
    addressTouched: false,
  });

  //validation
  useEffect(() => {
    if (clientNameValue.trim() && !isTouched.clientNameTouched) {
      setIsTouched((prev) => ({ ...prev, clientNameTouched: true }));
    }

    if (isTouched.clientNameTouched) {
      if (clientNameValue.trim()) {
        setValidationObject((v) => ({
          ...v,
          requiredClientName: true,
        }));
      } else {
        setValidationObject((v) => ({
          ...v,
          requiredClientName: false,
        }));
      }
    }
  }, [clientNameValue, isTouched]);

  useEffect(() => {
    if (clientPhoneValue.trim() && !isTouched.clientPhoneTouched) {
      setIsTouched((prev) => ({ ...prev, clientPhoneTouched: true }));
    }

    if (isTouched.clientPhoneTouched) {
      if (clientPhoneValue.trim()) {
        setValidationObject((v) => ({
          ...v,
          requiredClientPhone: true,
        }));
      } else {
        setValidationObject((v) => ({
          ...v,
          requiredClientPhone: false,
        }));
      }
    }
  }, [clientPhoneValue, isTouched]);

  useEffect(() => {
    if (addressValue.trim() && !isTouched.addressTouched) {
      setIsTouched((prev) => ({ ...prev, addressTouched: true }));
    }

    if (isTouched.addressTouched) {
      if (addressValue.trim()) {
        setValidationObject((v) => ({
          ...v,
          requiredAddress: true,
        }));
      } else {
        setValidationObject((v) => ({
          ...v,
          requiredAddress: false,
        }));
      }
    }
  }, [addressValue, isTouched]);

  useEffect(() => {
    if (
      currentCart?.discount?.value! < 0 ||
      currentCart?.discount?.value! > 100
    ) {
      setValidationObject((v) => {
        return { ...v, validDiscount: false };
      });
    } else {
      setValidationObject((v) => {
        return { ...v, validDiscount: true };
      });
    }

    if (
      currentCart?.servicePaymentAmount?.value! < 0 ||
      (currentCart?.servicePaymentAmount?.isInPercents === true &&
        currentCart?.servicePaymentAmount?.value! > 100)
    ) {
      setValidationObject((v) => {
        return { ...v, validServicePayment: false };
      });
    } else {
      setValidationObject((v) => {
        return { ...v, validServicePayment: true };
      });
    }
  }, [currentCart]);

  // Search panel
  const searchName = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.productsFilter?.name,
  );
  const {
    value: searchValue,
    setValue: setSearchValue,
    changeValueHandler: onChangeSearchValue,
  } = useInputChange(searchName || "");

  //clear search panel then you had chosen category
  useEffect(() => {
    setSearchValue(searchName || "");
    // eslint-disable-next-line
  }, [searchName]);

  const onClearSearch = () => {
    dispatch(dOrdersClearProductFiltersAC());
    setSearchValue("");
  };
  const onEnterSearch = () => {
    if (searchValue.trim()) {
      const filters: typeProductsFilters = {
        name: searchValue.trim(),
      };
      dispatch(dOrdersSetProductFiltersAC(filters));
    } else {
      dispatch(dOrdersClearProductFiltersAC());
    }
  };

  const onSubmitAddHandler = (e: FormEvent) => {
    e.preventDefault();

    // const getUserObject = (id: string | null) => {
    //     const user = selectUsersList.find((item) => item.id === id)
    //     return user ? {
    //         id: user.id,
    //         name: user.name
    //     } : null
    // }

    const mappedProducts = (): typeOrdersProductItem[] => {
      const temp: any = {};
      currentCart!.productList.forEach((item) => {
        if (temp[item.product.id]) {
          if (item.marked && item.labels) {
            temp[item.product.id] = {
              ...temp[item.product.id],
              amount: temp[item.product.id].amount + 1,
              markedLabels: temp[item.product.id].markedLabels
                ? [...temp[item.product.id].markedLabels, item.labels[0]]
                : [item.labels[0]],
              labels: temp[item.product.id].markedLabels
                ? [...temp[item.product.id].markedLabels, item.labels[0]]
                : [item.labels[0]],
            };
          }
        } else {
          temp[item.product.id] =
            item.labels && item.labels.length > 0
              ? {
                  ...item,
                  markedLabels: [...item.labels],
                }
              : item;
        }
        temp[item.product.id] = {
          ...temp[item.product.id],
          product: {
            ...temp[item.product.id].product,
            marked: item.marked,
          },
        };
      });
      // @ts-ignore
      return Object.values(temp).filter((item) => item.amount > 0);
    };

    const orderedProducts = mappedProducts();

    const hasEmptyPackageCode = orderedProducts.find(
      // @ts-ignore
      (item) => !item.product?.packageCode?.trim(),
    );

    if (hasEmptyPackageCode) {
      const id = new Date();

      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: t`The packaging code cannot be empty for the product "${hasEmptyPackageCode.product.name}"`,
        }),
      );

      return;
    }

    const newOrder: typeNewOrderRequest = {
      storeId: selectedStore!.id,
      customerName: clientNameValue.trim(),
      phone: clientPhoneValue.trim(),
      address: addressValue.trim(),
      comment: commentValue.trim(),
      courierId: courierIdValue === NULL_VALUE.id ? null : courierIdValue,
      collectorId: null,
      discount: {
        isInPercents: currentCart!.discount!.isInPercents,
        value: currentCart!.discount!.isInPercents
          ? currentCart!.discount!.value / 100
          : currentCart!.discount!.value,
      },
      servicePayment: {
        isInPercents: currentCart!.servicePaymentAmount!.isInPercents,
        value: currentCart!.servicePaymentAmount!.isInPercents
          ? currentCart!.servicePaymentAmount!.value / 100
          : currentCart!.servicePaymentAmount!.value,
      },
      orderedProducts,
      status: ORDERSTATUS.CREATED,
    };

    setOpenPopup(false);

    // @ts-ignore
    dispatch(
      dOrdersAddItemSagaAC(newOrder, filters, {
        page: pagination.pageNumber,
        size: pagination.itemsPerPage!,
      }),
    );
    dispatch(dOrdersClearCartItemAC());
  };

  const isDisabled =
    !validationObject.requiredClientName ||
    !validationObject.requiredAddress ||
    !validationObject.requiredClientPhone ||
    !isTouched.clientPhoneTouched ||
    !isTouched.addressTouched ||
    !isTouched.clientNameTouched ||
    !currentCart ||
    currentCart.productList.length === 0 ||
    currentCart.productList.some((item) => item.amount === 0) ||
    !validationObject.validDiscount ||
    !validationObject.validServicePayment;

  return (
    <>
      <form
        className="relative w-full "
        onSubmit={(event) => onSubmitAddHandler(event)}
      >
        <div className={"grid md:grid-cols-3 gap-x-4 border-t "}>
          <div className="md:col-span-3 flex flex-wrap gap-x-2 py-2   border-b">
            <div className="flex gap-x-2 self-center  text-sm font-medium text-gray-700">
              <Trans>Store:</Trans>
            </div>
            <div
              className={`flex  flex-wrap gap-x-2 text-lg font-medium ${
                selectedStore ? "text-gray-900" : "text-red-500"
              } cursor-pointer `}
              onClick={() => setOpenPopupSelectStore(true)}
            >
              <div
                className={"border-b border-transparent hover:border-gray-200"}
              >
                {" "}
                {selectedStore ? (
                  selectedStore.name
                ) : (
                  <Trans>Select store !</Trans>
                )}
              </div>
              <PencilAltIcon className={"w-4 h-4"} />
            </div>
          </div>
          <div className={"flex flex-col sm:border-r sm:pr-4"}>
            <div className="flex flex-col  py-2  ">
              <div className="flex gap-x-2  text-sm font-medium text-gray-700">
                <TagIcon className={" w-4 h-4"} />
                <Trans>Creator name:</Trans>
              </div>
              <div className="text-gray-90 sm:text-sm text-left border rounded-md block w-full px-3 py-2 shadow-sm bg-gray-100">
                {creatorName}
              </div>
            </div>

            <div className="flex flex-col   py-2 relative">
              <div className="flex gap-x-2  text-sm font-medium text-gray-700">
                <UserAddIcon className={" w-4 h-4"} />
                <Trans>Courier name:</Trans>
                <div className={"relative h-6 w-6"}>
                  {loaderUsersList && <Loader size={4} />}
                </div>
              </div>
              <SelectBox
                valuesList={selectAllUsersList}
                valueId={courierIdValue}
                setIdValue={setCourierIdValue}
                labelClassname="text-left"
              />
              {selectAllUsersList.length === 0 && (
                <div className={"flex mt-1 "}>
                  <ExclamationCircleIcon
                    className={"w-4 h-4 mr-1 text-red-400 "}
                  />
                  <p className={"text-xs  text-gray-500 flex-grow"}>
                    <Trans>You have not users with position 'Courier'.</Trans>
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className={"sm:col-span-2 grid sm:grid-cols-2 gap-x-2"}>
            <div className="flex flex-col   py-2">
              <div className="flex gap-x-1 text-sm font-medium text-gray-700">
                <span className="text-red-500">*</span>
                <div className="flex gap-x-2  text-sm font-medium text-gray-700">
                  <UserIcon className={" w-4 h-4"}/>
                  <Trans>Client name:</Trans>
                </div>
              </div>
              <InputText
                  id={"clientName"}
                  onChange={(e) => clientNameChangeValueHandler(e)}
                  errorMessage={
                    validationObject.requiredClientName ? undefined : (
                        <Trans>Required field</Trans>
                    )
                  }
                  value={clientNameValue}
                  required={true}
                  maxLength={100}
              />
            </div>
            <div className="flex flex-col   py-2">
              <div className="flex gap-x-1 text-sm font-medium text-gray-700">
                <span className="text-red-500">*</span>
                <div className="flex gap-x-2  text-sm font-medium text-gray-700">
                  <PhoneIcon className={" w-4 h-4"}/>
                  <Trans>Client phone:</Trans>
                </div>
              </div>
              <InputText
                  id={"clientPhone"}
                  onChange={(e) => clientPhoneChangeValueHandler(e)}
                  errorMessage={
                    validationObject.requiredClientPhone ? undefined : (
                        <Trans>Required field</Trans>
                    )
                  }
                  value={clientPhoneValue}
                  maxLength={25}
              />
            </div>

            <div className="flex flex-wrap gap-x-2 py-2 ">
              <div className={"flex-grow"}>

                <div className="flex gap-x-1 text-sm font-medium text-gray-700">
                  <span className="text-red-500">*</span>
                  <div className="flex gap-x-2  text-sm font-medium text-gray-700">
                    <LocationMarkerIcon className={" w-4 h-4"}/>
                    <Trans>Address :</Trans>
                  </div>
                </div>
                <InputTextarea
                    rows={3}
                    id={"address"}
                    onChange={(e) => addressChangeValueHandler(e)}
                    name={"address"}
                    errorMessage={
                      validationObject.requiredAddress ? undefined : (
                          <Trans>Required field</Trans>
                      )
                    }
                    value={addressValue}
                    maxLength={250}
                />
              </div>
            </div>
            <div className=" flex flex-wrap gap-x-2 py-2 justify-between ">
              <div className={"flex-grow"}>
                <div className="flex gap-x-2  text-sm font-medium text-gray-700">
                  <ChatAltIcon className={" w-4 h-4"} />
                  <Trans>Comment:</Trans>
                </div>
                <InputTextarea
                  id={"comment"}
                  rows={3}
                  onChange={(e) => commentChangeValueHandler(e)}
                  name={"comment"}
                  value={commentValue}
                  maxLength={250}
                />
              </div>
            </div>
          </div>
        </div>

        {selectedStore && (
          <div className="grid grid-cols-none grid-rows-5 lg:grid-rows-none lg:grid-cols-2 h-full lg:divide-x divide-y ">
            <div className="relative row-span-2 lg:row-auto flex flex-col sm:divide-y border-t">
              <div className="p-2 pb-0 sm:p-4">
                <InputSearch
                  id="s1"
                  variant="variant"
                  name="name"
                  value={searchValue}
                  onChange={onChangeSearchValue}
                  onKeyEnterDown={onEnterSearch}
                  onClear={onClearSearch}
                  placeholder={t`Search by product's name`}
                />
              </div>

              <div className="relative flex-grow grid grid-cols-2 row-span-2 lg:row-auto divide-x">
                <div className="relative hidden sm:block">
                  <div className="relative flex flex-col p-2 sm:p-4 h-full">
                    <Categories
                      categoryList={categoryList}
                      loader={loaderCategoryList}
                    />
                  </div>
                </div>
                <div className="relative col-span-2 sm:col-auto">
                  <div className="relative flex flex-col p-2 sm:p-4 h-full">
                    <Products
                      categoryList={categoryList}
                      loaderCategoryList={loaderCategoryList}
                      setOpenEnterMarkLabel={setOpenEnterMark}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="relative row-span-3 lg:row-auto">
              <div className="relative flex flex-col p-2 sm:p-4 h-full">
                <Carts
                  setOpenChangePrise={setOpenChangePrise}
                  setOpenPopupDeleteMarkedProduct={setOpenDeleteMarkedProduct}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mt-5 sm:mt-4 flex-row-reverse ">
          <Button
            type="submit"
            disabled={isDisabled}
            variant={variant}
            label={<Trans>Add</Trans>}
            onClick={(e) => onSubmitAddHandler(e)}
          />
          <Button
            type="button"
            secondary={true}
            label={<Trans>Cancel</Trans>}
            onClick={() => setOpenPopup(false)}
          />
        </div>
      </form>

      <PopupPanelDashboard
        title={<Trans>Change price</Trans>}
        openPopup={isOpenChangePrise}
        setOpenPopup={setOpenChangePrise}
      >
        <PopupChangePrice setOpenPopup={setOpenChangePrise} />
      </PopupPanelDashboard>

      <PopupPanelDashboard
        title={<Trans>Enter markLabel</Trans>}
        openPopup={isOpenEnterMark}
        setOpenPopup={setOpenEnterMark}
      >
        <PopupEnterMark setOpenPopup={setOpenEnterMark} />
      </PopupPanelDashboard>

      <PopupPanelDashboard
        title={<Trans>Enter markLabel for delete product</Trans>}
        openPopup={isOpenDeleteMarkedProduct}
        setOpenPopup={setOpenDeleteMarkedProduct}
        popupContainerClassName={"w-96"}
      >
        <PopupDeleteMarkedProduct setOpenPopup={setOpenDeleteMarkedProduct} />
      </PopupPanelDashboard>

      {/* Popup select store for order dialog */}
      <PopupPanelDashboard
        title={<Trans>Select store for order</Trans>}
        openPopup={openPopupSelectStore}
        setOpenPopup={setOpenPopupSelectStore}
      >
        <PopupSelectStore setOpenPopup={setOpenPopupSelectStore} />
      </PopupPanelDashboard>
    </>
  );
};
